import type { FlexProps } from '@chakra-ui/react';

/**
 * Shared look for the purple lightning chip that marks configured automation.
 *
 * Several surfaces hand-roll this chip (the stage picker's marker, the
 * candidate drawer's chip, `AutomationBadge`), so the hover treatment lives
 * here rather than being copied five times.
 *
 * The hover is a lighter fill plus a 1px gradient ring (Figma). `border-color`
 * cannot take a gradient, so it needs the two-layer background trick: layer one
 * is the fill clipped to the padding box, layer two the gradient clipped to the
 * border box, showing through a transparent border.
 *
 * `--kula-colors-*` are the token CSS vars — this repo sets `cssVarPrefix:
 * 'kula'`, so `--chakra-colors-*` silently resolves to nothing.
 *
 * The raw background is exported too, for the one site that cannot take the
 * props object: a kula `KButton` whose `sx` needs `!important` to beat the
 * button's own variant styles.
 */
export const AUTOMATION_CHIP_HOVER_BACKGROUND = [
  'linear-gradient(var(--kula-colors-pastel-3-lightest), var(--kula-colors-pastel-3-lightest)) padding-box',
  'linear-gradient(180deg, var(--kula-colors-primary-300) 0%, var(--kula-colors-pastel-3-dark) 100%) border-box',
].join(', ');

/**
 * Spread onto the chip's container. The transparent border is carried at REST
 * too: the chip would otherwise gain 1px on hover and jitter its contents.
 */
export const automationChipHoverProps: Pick<FlexProps, 'border' | '_hover'> = {
  border: '1px solid transparent',
  _hover: { background: AUTOMATION_CHIP_HOVER_BACKGROUND },
};
